import React from "react";
import { useNavigate } from "react-router-dom";
import {
  PrioritizedIssue,
  type AuthorityOverview,
} from "../hooks/useMCPContext";
import { Badge } from "@/components/ui/badge";
import { AlertCircle, Folder, Loader2 } from "lucide-react";

const ISSUE_BADGES = [
  {
    key: "high" as const,
    label: "High",
    className:
      "border border-red-500/20 bg-red-500/15 text-red-400 hover:bg-red-500/15",
  },
  {
    key: "medium" as const,
    label: "Med",
    className:
      "border border-amber-500/20 bg-amber-500/15 text-amber-400 hover:bg-amber-500/15",
  },
  {
    key: "low" as const,
    label: "Low",
    className:
      "border border-green-500/20 bg-green-500/15 text-green-400 hover:bg-green-500/15",
  },
];

interface RubyWelcomeMessageProps {
  cardId: string;
  overview: AuthorityOverview | null;
  priorityQueue: PrioritizedIssue[] | null;
  allIssues?: PrioritizedIssue[] | null;
  mcpLoading: boolean;
  mcpError: string | null;
}

const RubyWelcomeMessage: React.FC<RubyWelcomeMessageProps> = ({
  cardId,
  overview,
  priorityQueue,
  allIssues,
  mcpLoading,
  mcpError,
}) => {
  const navigate = useNavigate();
  const card = overview?.cards[cardId];
  const assistantName = card?.cardName ? `${card.cardName} assistant` : "AI fix assistant";
  const cardName = card?.cardName ?? cardId;

  const issueSource =
    allIssues && allIssues.length > 0 ? allIssues : (priorityQueue ?? []);
  const pendingForCard = issueSource.filter(
    (issue) => issue.card === cardId && issue.status !== "fixed"
  );
  const highCount = pendingForCard.filter((issue) => issue.priority === "HIGH").length;
  const mediumCount = pendingForCard.filter((issue) => issue.priority === "MEDIUM").length;
  const lowCount = pendingForCard.filter((issue) => issue.priority === "LOW").length;
  const totalCount = pendingForCard.length;
  const counts = { high: highCount, medium: mediumCount, low: lowCount };

  const recommendations = overview?.recommendations;
  const topIssue = recommendations?.status === "structured"
    ? priorityQueue?.find((i) => i.status === "pending")
    : undefined;

  const showCrossCard = Boolean(topIssue && topIssue.card !== cardId && overview);
  const isBlocking = showCrossCard && totalCount > 0;

  if (mcpLoading) {
    return <p className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" style={{ color: "#8B7BF7" }} /> Loading...</p>;
  }

  if (mcpError) {
    return <p className="flex items-center gap-2"><AlertCircle className="w-4 h-4" style={{ color: "#F87171" }} /> Error: {mcpError}</p>;
  }

  const notice = overview?.reportNotice ? (
    <p className="text-xs leading-relaxed" style={{ color: "#FBBF24" }}>{overview.reportNotice}</p>
  ) : null;

  if (recommendations?.status === "saved_priority") {
    return (
      <div className="space-y-2.5">
        {notice}
        <p>👋 Hey! I&apos;m Ruby, your {assistantName}. This report has a saved priority, not a ranked action list.</p>
        <p className="text-xs leading-relaxed" style={{ color: "#E8E4FF" }}>{recommendations.narrative}</p>
        <p className="flex items-center gap-1.5 text-xs" style={{ color: "#9AA0B3" }}>
          <Folder className="h-3.5 w-3.5 flex-none" fill="#FBBF24" stroke="#FBBF24" />
          Your chat history is saved — pick up anytime.
        </p>
      </div>
    );
  }

  if (recommendations?.status === "unavailable") {
    return (
      <div className="space-y-2.5">
        {notice}
        <p>👋 Hey! I&apos;m Ruby, your {assistantName}.</p>
        <p>Recommendations unavailable for this report.</p>
      </div>
    );
  }

  return (
    <div className="space-y-2.5">
      {notice}
      <p>👋 Hey! I&apos;m Ruby, your {assistantName}. I found <strong>{totalCount}</strong> issues on this card:</p>

      {totalCount > 0 && (
        <>
          <div className="flex flex-wrap gap-1.5">
            {ISSUE_BADGES.map(({ key, label, className }) =>
              counts[key] > 0 ? (
                <Badge
                  key={key}
                  className={`h-6 rounded-md px-2 py-0 text-[10px] font-semibold leading-none gap-1 ${className}`}
                >
                  <span className="text-[11px] font-bold">
                    {counts[key]}
                  </span>
                  {label}
                </Badge>
              ) : null
            )}
          </div>
          {highCount > 0 && !isBlocking && (
            <p>Click below to see what we can fix together.</p>
          )}
        </>
      )}

      {highCount === 0 && totalCount === 0 && (
        <p>
          ✨ Great news! Your <strong>{cardName}</strong> is in good shape.
        </p>
      )}

      {showCrossCard && topIssue && (
        <div
          className="mt-3 pt-3 space-y-2"
          style={{ borderTop: "1px solid rgba(139,123,247,0.2)" }}
        >
          {isBlocking ? (
            <p>
              ⚠️ <strong>Work on {topIssue.cardName} next</strong> — Highest top priority is{" "}
              <strong>{topIssue.title}</strong> ({topIssue.priority} priority).
            </p>
          ) : (
            <p>
              Your top priority site-wide is on <strong>{topIssue.cardName}</strong>:{" "}
              <strong>{topIssue.title}</strong> ({topIssue.priority} priority).
            </p>
          )}
          {topIssue.description && (
            <p className="text-xs" style={{ color: "#9AA0B3" }}>
              {topIssue.description}
            </p>
          )}
          {topIssue.route && (
            <button
              type="button"
              onClick={() => navigate(topIssue.route)}
              className="px-4 py-2 mt-1 rounded-lg text-xs font-semibold transition-all hover:brightness-110"
              style={{ background: "#5B3DF5", color: "#fff" }}
            >
              🔧 Go to {topIssue.cardName} to fix them →
            </button>
          )}
        </div>
      )}

      {!showCrossCard && totalCount === 0 && (
        <p style={{ color: "#4ADE80" }}>
          🎉 Amazing! All your Smart Cards are in great shape. No issues to fix!
        </p>
      )}

      <p className="flex items-center gap-1.5 text-xs" style={{ color: "#9AA0B3" }}>
        <Folder className="h-3.5 w-3.5 flex-none" fill="#FBBF24" stroke="#FBBF24" />
        Your chat history is saved — pick up anytime.
      </p>
    </div>
  );
};

export default RubyWelcomeMessage;

